import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  startAfter,
  where,
} from 'firebase/firestore';
import type { DiscussionMessage, DiscussionReads } from '@allemnii-pulse/shared';
import { db } from '../../lib/firebase.js';
import { api } from '../../lib/api.js';
import { asDate } from '../../lib/time.js';
import { useDocument } from '../../lib/firestore.js';

/** The rules cap a discussion list at 50 per read (isPagedQuery). */
export const PAGE_SIZE = 50;

export function messagesPath(projectId: string): string {
  return `projects/${projectId}/discussions`;
}

const byCreatedAt = (a: DiscussionMessage, b: DiscussionMessage) =>
  asDate(a.createdAt).getTime() - asDate(b.createdAt).getTime();

/**
 * The discussion's messages, oldest first: the newest page live, plus older pages fetched once
 * as the user scrolls up (loadOlder). A message that new arrivals push out of the live window
 * moves into the older list rather than vanishing from view; one deleted from the window just
 * goes. Older pages don't update live — `refresh` re-reads one message after your own change to
 * it (a reaction, a pin) so it still updates at once.
 */
export function useDiscussionMessages(projectId: string) {
  const [live, setLive] = useState<DiscussionMessage[]>([]);
  const [older, setOlder] = useState<DiscussionMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setLive([]);
    setOlder([]);
    setLoading(true);
    const col = collection(db, messagesPath(projectId));
    return onSnapshot(
      query(col, orderBy('createdAt', 'desc'), limit(PAGE_SIZE)),
      (snap) => {
        setLive(snap.docs.map((d) => d.data() as DiscussionMessage));
        setHasMore((prev) => prev || snap.size === PAGE_SIZE);
        setLoading(false);
        setError(null);
        for (const change of snap.docChanges()) {
          if (change.type !== 'removed') continue;
          const removed = change.doc.data() as DiscussionMessage;
          // Still there means it was pushed out of the window, not deleted.
          void getDoc(change.doc.ref).then((fresh) => {
            if (!fresh.exists()) return;
            setOlder((list) =>
              list.some((m) => m.id === removed.id)
                ? list
                : [...list, fresh.data() as DiscussionMessage].sort(byCreatedAt),
            );
          });
        }
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );
  }, [projectId]);

  const messages = useMemo(() => {
    const liveIds = new Set(live.map((m) => m.id));
    return [...older.filter((m) => !liveIds.has(m.id)), ...live].sort(byCreatedAt);
  }, [live, older]);

  const oldestRef = useRef<DiscussionMessage | undefined>(undefined);
  oldestRef.current = messages[0];

  const loadOlder = useCallback(async () => {
    const oldest = oldestRef.current;
    if (!oldest || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const snap = await getDocs(
        query(
          collection(db, messagesPath(projectId)),
          orderBy('createdAt', 'desc'),
          startAfter(oldest.createdAt),
          limit(PAGE_SIZE),
        ),
      );
      const page = snap.docs.map((d) => d.data() as DiscussionMessage);
      setOlder((list) => {
        const ids = new Set(list.map((m) => m.id));
        return [...list, ...page.filter((m) => !ids.has(m.id))].sort(byCreatedAt);
      });
      setHasMore(snap.size === PAGE_SIZE);
    } finally {
      setLoadingOlder(false);
    }
  }, [projectId, loadingOlder]);

  const refresh = useCallback(
    async (messageId: string) => {
      const snap = await getDoc(doc(db, messagesPath(projectId), messageId));
      setOlder((list) =>
        snap.exists()
          ? list.map((m) => (m.id === messageId ? (snap.data() as DiscussionMessage) : m))
          : list.filter((m) => m.id !== messageId),
      );
    },
    [projectId],
  );

  return { messages, loading, error, hasMore, loadingOlder, loadOlder, refresh };
}

/** The pinned messages, most recently pinned first — live, and independent of how far back the
 * chat itself has been scrolled. */
export function usePinnedMessages(projectId: string): DiscussionMessage[] {
  const [pinned, setPinned] = useState<DiscussionMessage[]>([]);
  useEffect(
    () =>
      onSnapshot(
        query(
          collection(db, messagesPath(projectId)),
          where('pinnedAt', '!=', null),
          orderBy('pinnedAt', 'desc'),
          limit(PAGE_SIZE),
        ),
        (snap) => setPinned(snap.docs.map((d) => d.data() as DiscussionMessage)),
        () => setPinned([]),
      ),
    [projectId],
  );
  return pinned;
}

function readsPath(uid: string): string {
  return `users/${uid}/prefs/discussionReads`;
}

/** When `uid` last read `projectId`'s discussion — null if never. */
export function useLastRead(
  uid: string | undefined,
  projectId: string,
): {
  lastRead: Date | null;
  loading: boolean;
} {
  const { data, loading } = useDocument<DiscussionReads>(uid ? readsPath(uid) : null);
  const value = data?.[projectId];
  return { lastRead: value ? asDate(value) : null, loading };
}

/** Moves the read marker forward to `at` (never back). Client-written UX state, like the theme
 * preference — users/{uid}/prefs is the one place the rules let the client write. */
export async function markRead(uid: string, projectId: string, at: Date): Promise<void> {
  await setDoc(doc(db, readsPath(uid)), { [projectId]: Timestamp.fromDate(at) }, { merge: true });
}

/**
 * Messages from other people since you last read the discussion — the tab's badge. Capped at one
 * page (shown as "50+"): the badge says "there's something new", not an exact tally.
 */
export function useUnreadCount(
  uid: string | undefined,
  projectId: string,
  enabled: boolean,
): { count: number; capped: boolean } {
  const { lastRead, loading } = useLastRead(uid, projectId);
  const [result, setResult] = useState({ count: 0, capped: false });
  const since = lastRead?.getTime() ?? 0;

  useEffect(() => {
    if (!enabled || !uid || loading) {
      setResult({ count: 0, capped: false });
      return;
    }
    const constraints = since
      ? [where('createdAt', '>', Timestamp.fromMillis(since)), orderBy('createdAt', 'desc')]
      : [orderBy('createdAt', 'desc')];
    return onSnapshot(
      query(collection(db, messagesPath(projectId)), ...constraints, limit(PAGE_SIZE)),
      (snap) =>
        setResult({
          count: snap.docs.filter((d) => d.data().authorUid !== uid).length,
          capped: snap.size === PAGE_SIZE,
        }),
      () => setResult({ count: 0, capped: false }),
    );
  }, [enabled, uid, projectId, since, loading]);

  return result;
}

/** How long a signed attachment URL is used before asking for a fresh one — the API signs them
 * for an hour; this leaves a margin. */
const URL_TTL_MS = 50 * 60 * 1000;

/**
 * Signed read URLs for every attachment on `messages`, keyed by attachment id. Fetched in one
 * batch per new set of messages, cached for the URL lifetime.
 */
export function useAttachmentUrls(
  projectId: string,
  messages: DiscussionMessage[],
): Record<string, string> {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const fetchedAt = useRef(new Map<string, number>());

  const needed = messages
    .filter((m) => m.attachments.length > 0)
    .filter((m) => Date.now() - (fetchedAt.current.get(m.id) ?? 0) > URL_TTL_MS)
    .map((m) => m.id);
  const key = needed.join(',');

  useEffect(() => {
    if (needed.length === 0) return;
    const now = Date.now();
    needed.forEach((id) => fetchedAt.current.set(id, now));
    const batches: string[][] = [];
    for (let i = 0; i < needed.length; i += 100) batches.push(needed.slice(i, i + 100));
    for (const messageIds of batches) {
      api
        .post<{ urls: Record<string, string> }>(
          `/projects/${projectId}/discussions/attachment-urls`,
          { messageIds },
        )
        .then((res) => setUrls((prev) => ({ ...prev, ...res.urls })))
        .catch(() => messageIds.forEach((id) => fetchedAt.current.delete(id)));
    }
    // `key` stands in for `needed`, which is a fresh array every render.
  }, [key, projectId]);

  return urls;
}
